import { gql, useQuery } from '@apollo/client';

import { Server } from '../apollo/types';
import { Exact, InputMaybe, Scalars } from '../types';
import { NoteTypeEnum } from './client-information/types';
import {
  AppointmentLocation,
  AppointmentType,
  AppointmentsFilter,
  RecurringAppointmentGrouping,
} from './schedule-preview/types';
import { AppointmentObjectSortEnum, ClientUserAccountPauseReason } from './types';

/* eslint-disable @typescript-eslint/no-explicit-any */
export type TimelineAppointment = {
  __typename?: 'AppointmentObject';
  id: string;
  recurringAppointmentGroupingId?: number | null;
  timeStart: any;
  timeEnd: any;
  patientId?: number | null;
  appointmentShortId: string;
  appointmentType: AppointmentType;
  status: string;
  inOffice: AppointmentLocation;
  recurringAppointmentGrouping?: RecurringAppointmentGrouping | null;
  appointmentClaim?: {
    __typename?: 'ClaimObject';
    id: string;
    auditNote?: string | null;
    processNote?: string | null;
    progressNote?: string | null;
    payoutDate?: any | null;
    payoutMessage?: string | null;
    expectedPayout: any;
    totalPaidOut?: number | null;
    noteType?: string | null;
  } | null;
  notes?: Array<{
    __typename?: 'NoteObject';
    id: number;
    isDraft: boolean;
    noteType: NoteTypeEnum;
    noteSummary?: string | null;
  }> | null;
};

export type TimelineAppointmentEdge = {
  __typename?: 'AppointmentObjectEdge';
  cursor: string;
  node: TimelineAppointment;
};

export type GetTimelineAppointmentsQueryData = {
  __typename?: 'Query';
  currentProvider?: {
    __typename?: 'ProviderObject';
    id: string;
    providerAppointments?: {
      __typename?: 'AppointmentObjectConnection';
      totalCount?: number | null;
      edges: Array<TimelineAppointmentEdge>;
    } | null;
  } | null;
  patientChart?: {
    __typename?: 'PatientObject';
    firstName?: string | null;
    pronouns?: string | null;
    clientUser?: {
      __typename?: 'ClientUserObject';
      activePauseRecord?: {
        __typename?: 'ClientUserAccountPauseObject';
        pauseReason?: ClientUserAccountPauseReason;
      };
      eapCoverageCard?: {
        __typename?: 'EapCoverageCardObject';
        continuityOfCareAcknowledged?: boolean | null;
        remainingSessions?: number | null;
        payorName?: string | null;
        maxSessions?: number | null;
        payorLink?: string | null;
      } | null;
    };
  } | null;
};

export type GetTimelineAppointmentsQueryVariables = Exact<{
  num?: InputMaybe<Scalars['Int']['input']>;
  after?: InputMaybe<Scalars['String']['input']>;
  filters?: InputMaybe<AppointmentsFilter>;
  sort?: InputMaybe<Array<InputMaybe<AppointmentObjectSortEnum>> | InputMaybe<AppointmentObjectSortEnum>>;
  patientShortId: Scalars['ID']['input'];
  includePatientChart: Scalars['Boolean']['input'];
}>;

export const GET_TIMELINE_APPOINTMENTS_QUERY = gql`
  query GetTimelineAppointments(
    $num: Int
    $after: String
    $filters: AppointmentsFilter
    $sort: [AppointmentObjectSortEnum]
    $patientShortId: ID!
    $includePatientChart: Boolean!
  ) {
    currentProvider {
      id
      shortId
      providerAppointments(first: $num, after: $after, filters: $filters, sort: $sort) {
        totalCount
        edges {
          cursor
          node {
            id
            recurringAppointmentGroupingId
            appointmentShortId
            timeStart
            timeEnd
            patientId
            appointmentType
            status
            inOffice
            appointmentClaim {
              id
              auditNote
              processNote
              progressNote
              payoutDate
              payoutMessage
              expectedPayout
              totalPaidOut
              noteType
            }
            attachments {
              id
              fileName
              archivedAt
              presignedS3DownloadUrl
              attachmentType
            }
            notes {
              id
              isDraft
              noteType
              noteSummary
            }
            recurringAppointmentGrouping {
              indefinite
              recurringAppointmentGroupingId
              weeksBetween
              recurrenceRule
              version
              appointments {
                id
                timeStart
              }
            }
          }
        }
      }
    }
    patientChart(values: { patientShortId: $patientShortId }) @include(if: $includePatientChart) {
      id
      shortId
      firstName
      pronouns
      clientUser {
        activePauseRecord {
          pauseReason
        }
        eapCoverageCard {
          continuityOfCareAcknowledged
          remainingSessions
          payorName
          maxSessions
          payorLink
        }
      }
    }
  }
`;

export function useGetUpcomingAppointmentsQuery(patientShortId: string, timeStart: string) {
  return useQuery<GetTimelineAppointmentsQueryData, GetTimelineAppointmentsQueryVariables>(
    GET_TIMELINE_APPOINTMENTS_QUERY,
    {
      context: { server: Server.MONOLITH },
      variables: {
        filters: {
          patientShortIds: [patientShortId],
          timeStartGte: timeStart,
          canceled: false,
        },
        sort: [AppointmentObjectSortEnum.TIME_START_ASC],
        patientShortId,
        includePatientChart: Boolean(patientShortId),
      },
    },
  );
}
